// ════════════════════════════════════════════════════════════════════════
// MRSS 2.0 — MOTEUR DE CALCUL PUR (Melbourne Return to Sport Score)
// Cooper & Hughes, Melbourne ACL Rehabilitation Guide 2.0.
//
// Découplé de l'UI : aucune dépendance React, aucun accès réseau/DOM.
// Formule reprise à l'identique de l'ancien formulaire MRSSView (App.js) :
//   A /10  (épanchement, pivot shift, flexion, extension — brut /20 ÷ 2)
//   B /20  (IKDC /10 + ACL-RSI 10 pts si ≥ 90 %)
//   C      TSK-11 ≤ 18 — éliminatoire
//   D /50  (SEBT 10, vestibulaire 5+5, single/triple/crossover/side hop 5 chacun, single leg rise 10)
//   E      2 tests de condition physique ≥ pré-blessure — éliminatoire
//   F /20  (4 hop tests sous fatigue, 5 chacun)
//
// Hop tests (décision D2, 15/10/2026) : ESSAIS BRUTS stockés, LSI calculé ici.
//   LSI = meilleur essai côté opéré ÷ meilleur essai côté sain × 100
//   (moyenne des essais pour le SEBT, comme dans le formulaire d'origine).
//
// ── Contrat d'entrée `inputs` (construit par buildMrssInputs, src/measureMap.js) ──
//   ep, piv, flex, ext       : points 0-5 (ou undefined)
//   ikdc, rsi, tsk           : scores bruts (ou undefined)
//   dom                      : true si côté opéré = dominant
//   trials.{sebt,sh,th,ch,sideh,rise,fsh,fth,fch,fsideh} : { inj:[..], healthy:[..] }
//   vest_lat, vest_hb, e1, e2: true | false | undefined
//   prevLCA, prevDelai       : true | false | undefined
// ════════════════════════════════════════════════════════════════════════

export const MRSS_THRESHOLD = 95;

export const MRSS_D_TESTS = [
  { key: "sebt", maxPts: 10, useAvg: true },
  { key: "sh", maxPts: 5 },
  { key: "th", maxPts: 5 },
  { key: "ch", maxPts: 5 },
  { key: "sideh", maxPts: 5 },
  { key: "rise", maxPts: 10 },
];
export const MRSS_F_TESTS = [
  { key: "fsh", maxPts: 5 },
  { key: "fth", maxPts: 5 },
  { key: "fch", maxPts: 5 },
  { key: "fsideh", maxPts: 5 },
];

const has = v => v !== undefined && v !== null && v !== "" && !Number.isNaN(v);

// Barème LSI → points /10 (MRSS 2.0 original — dépend de la dominance du côté opéré)
export function lsiTo10(lsi, dominant) {
  if (!has(lsi)) return 0;
  if (dominant) {
    if (lsi >= 97 && lsi <= 105) return 10;
    if ((lsi >= 90 && lsi < 97) || (lsi > 105 && lsi <= 110)) return 8;
    if ((lsi >= 80 && lsi < 90) || (lsi > 110 && lsi <= 120)) return 6;
    if ((lsi >= 70 && lsi < 80) || (lsi > 120 && lsi <= 130)) return 4;
    if ((lsi >= 60 && lsi < 70) || (lsi > 130 && lsi <= 140)) return 2;
    return 0;
  }
  if (lsi >= 95 && lsi <= 103) return 10;
  if ((lsi >= 85 && lsi < 95) || (lsi > 103 && lsi <= 110)) return 8;
  if ((lsi >= 75 && lsi < 85) || (lsi > 110 && lsi <= 120)) return 6;
  if ((lsi >= 65 && lsi < 75) || (lsi > 120 && lsi <= 130)) return 4;
  if ((lsi >= 55 && lsi < 65) || (lsi > 130 && lsi <= 140)) return 2;
  return 0;
}

// LSI à partir des essais bruts (null si un côté manque)
export function trialsLsi(trials, useAvg = false) {
  const inj = (trials?.inj || []).map(Number).filter(x => has(x));
  const healthy = (trials?.healthy || []).map(Number).filter(x => has(x));
  if (!inj.length || !healthy.length) return null;
  const pick = arr => (useAvg ? arr.reduce((a, b) => a + b, 0) / arr.length : Math.max(...arr));
  const h = pick(healthy);
  if (h <= 0) return null;
  return +((pick(inj) / h) * 100).toFixed(1);
}

function testPts(trials, maxPts, useAvg, dominant) {
  const lsi = trialsLsi(trials, useAvg);
  if (lsi === null) return { lsi: null, pts: 0, measured: false };
  const p10 = lsiTo10(lsi, dominant);
  return { lsi, pts: maxPts === 10 ? p10 : p10 / 2, measured: true };
}

export function computeMrss(inputs = {}) {
  const dom = !!inputs.dom;
  const trials = inputs.trials || {};

  // Partie A
  const aVals = [inputs.ep, inputs.piv, inputs.flex, inputs.ext];
  const aFilled = aVals.every(has);
  const scA = aFilled ? aVals.reduce((s, x) => s + Number(x), 0) / 2 : null;

  // Partie B
  const ikdcScore = has(inputs.ikdc) ? Number(inputs.ikdc) : null;
  const rsiScore = has(inputs.rsi) ? Number(inputs.rsi) : null;
  const ikdcPts = ikdcScore !== null ? Math.min(10, ikdcScore / 10) : null;
  const rsiPts = rsiScore !== null ? (rsiScore >= 90 ? 10 : 0) : null;
  const scB = ikdcPts !== null || rsiPts !== null ? (ikdcPts || 0) + (rsiPts || 0) : null;

  // Partie C
  const tsk = has(inputs.tsk) ? Number(inputs.tsk) : null;
  const tskPass = tsk !== null ? tsk <= 18 : null;

  // Partie D
  const details = {};
  let scD = 0;
  for (const t of MRSS_D_TESTS) {
    details[t.key] = testPts(trials[t.key], t.maxPts, !!t.useAvg, dom);
    scD += details[t.key].pts;
  }
  const vest = (inputs.vest_lat === true ? 5 : 0) + (inputs.vest_hb === true ? 5 : 0);
  scD += vest;
  details.vest = { pts: vest, measured: has(inputs.vest_lat) && has(inputs.vest_hb) };

  // Partie E
  const ePass = has(inputs.e1) && has(inputs.e2) ? inputs.e1 === true && inputs.e2 === true : null;

  // Partie F
  let scF = 0;
  for (const t of MRSS_F_TESTS) {
    details[t.key] = testPts(trials[t.key], t.maxPts, false, dom);
    scF += details[t.key].pts;
  }

  const total = (scA || 0) + (scB || 0) + scD + scF;

  // Complet = toutes les composantes scorées renseignées (sinon le total n'est pas
  // un MRSS valide et ne doit pas alimenter le hurdle RTR-SPORT H5).
  const complete = scA !== null && ikdcScore !== null && rsiScore !== null
    && [...MRSS_D_TESTS, ...MRSS_F_TESTS].every(t => details[t.key].measured)
    && details.vest.measured;

  let verdict = "incomplete";
  if (tsk !== null && !tskPass) verdict = "tsk_fail";
  else if (ePass === false) verdict = "e_fail";
  else if (total >= MRSS_THRESHOLD && tskPass === true && ePass === true && inputs.prevLCA === true && inputs.prevDelai === true) verdict = "green";
  else if (total >= MRSS_THRESHOLD && tskPass === true && ePass === true) verdict = "wait";
  else if (total > 0) verdict = "no";

  return { scA, ikdcScore, ikdcPts, rsiScore, rsiPts, scB, tsk, tskPass, scD, ePass, scF, total, complete, verdict, details };
}

export default { computeMrss, lsiTo10, trialsLsi };
